import {FILL_IT, UPLOAD, SEND_LATER} from 'constants/wizard/prescriptionType';
import {emptyByDefault} from 'libs/EEC/productToEECProduct';
import {pathOr, propEq, propOr} from 'ramda';
import {BADGES} from 'constants/badges';

import {PreparedProductType} from './productPageDataToProductType';

/**
 * Convert wizard and meta data to prescription values.
 *
 * @param {Object} wizard - Wizard data.
 * @param {Object} meta - Meta data.
 * @returns {Object} - Prescription values.
 */
export const getPrescriptionValues = (wizard, meta) => {
    const PRESCRIPTION_TYPES = {
        [FILL_IT]: 'online',
        [UPLOAD]: 'upload',
        [SEND_LATER]: 'email',
    };

    // fashion glasses
    if (!meta.prescription) return {};

    // glasses with prescription
    if (!meta.method) {
        const {method, name} = meta.prescription;
        return {type: PRESCRIPTION_TYPES[method], name};
    }

    const {contact_lens_colors: contactLensColors} = wizard;

    // contact lenses without colors
    if (!contactLensColors) return {type: PRESCRIPTION_TYPES[meta.method]};
    // contact lenses with colors
    return {
        type: PRESCRIPTION_TYPES[meta.method],
        od: {
            color: contactLensColors
                ? contactLensColors.find(({value}) => value === meta.prescription.od.color).label
                : '',
        },
        os: {
            color: contactLensColors
                ? contactLensColors.find(({value}) => value === meta.prescription.os.color).label
                : '',
        },
    };
};

/**
 * Get raw custom options.
 *
 * @param {Object} meta - Meta data.
 * @param {Object} wizard - Wizard data.
 * @returns {Array<{name: string, title: string}>} - Custom options.
 */
export const getRawCustomOptions = (meta, wizard) => {
    if (!meta.options || wizard.wizardMock.usage.length === 0) return null;

    const {optionId} = wizard.wizardMock.usage[0];

    const lensTypeId = meta.options[optionId];
    const selectedOption = wizard.wizardMock.usage.find(option => +option.id === +lensTypeId);
    return [{name: 'Lens Type', title: (selectedOption || {subtitle: ''}).subtitle}];
};

/**
 * Convert data from PDP to QuoteItemType.
 *
 * @param {object} product - Added product.
 * @returns {PreparedProductType} - Prepared Product.
 */
export const prepareProductDataToEEC = ({
    activeProduct,
    product,
    price,
    wizard,
    payload,
    meta,
    quoteItem,
}): PreparedProductType => ({
    id: emptyByDefault(payload.data.id),
    qty: meta.qty || 1,
    price: price || activeProduct.price,
    total: emptyByDefault(payload.data.total),
    parent_name: emptyByDefault(product.parent_name || product.name),
    product_id: emptyByDefault(activeProduct.id),
    description_length: propOr(0, 'length', activeProduct.description),
    gender: activeProduct.gender_label || [],
    brand: emptyByDefault((product.brand || {name: ''}).name),
    product_sku: emptyByDefault(activeProduct.sku),
    sku: emptyByDefault(payload.data.sku || activeProduct.sku),
    type: emptyByDefault(product.type),
    manufacturer: emptyByDefault(product.manufacturer_value),
    prescription: getPrescriptionValues(wizard, meta),
    frame: {
        frameSize: emptyByDefault(activeProduct.frame_size_value),
        price: emptyByDefault(activeProduct.price),
        color: activeProduct.color_value ? activeProduct.color_value.join(' / ') : '',
    },
    lensType: emptyByDefault(activeProduct.contact_lens_type_label),
    is_eligible_for_hto: !!activeProduct.eligible_for_hto,
    productAsHto: !!meta.productAsHTO,
    custom_options: getRawCustomOptions(meta, wizard),
    isPremium: !!quoteItem.isPremium,
    pack: pathOr({}, ['pack'], quoteItem),
    discounted_price: quoteItem.discounted_price,
    options: pathOr({}, ['options'], quoteItem),
});

/**
 * Preparing data for addToCart event for added products using wizardMs
 *
 * @param {object} product - PDP, wizard and add to cart endpoint response data
 * @returns {PreparedProductType} - Prepared Product
 */
export const prepareWizardMsProductDataToEEC = ({
    activeProduct,
    product,
    wizard,
    addToCartData: data,
    addToCartMeta: meta,
    activePackage,
    activeUsage,
}): PreparedProductType => ({
    id: emptyByDefault(activeProduct.id),
    qty: propOr(1, 'qty', meta),
    price: data.total || 0,
    total: activeProduct.price || 0,
    parent_name: emptyByDefault(activeProduct.parentName || activeProduct.name),
    product_id: emptyByDefault(activeProduct.id),
    description_length: pathOr(0, ['description', 'length'], activeProduct),
    gender: activeProduct.genders || [],
    brand: pathOr('', ['brand', 'label'], product),
    product_sku: emptyByDefault(activeProduct.sku),
    sku: emptyByDefault(data.sku || activeProduct.sku),
    type: emptyByDefault(product.type),
    manufacturer: emptyByDefault(product.manufacturerValue),
    frame: {
        frameSize: emptyByDefault(pathOr('', ['frame', 'size', 'shortLabel'], activeProduct)),
        price: emptyByDefault(activeProduct.price),
        color: pathOr('', ['color', 'label'], activeProduct),
    },
    lensType: pathOr('', ['contactLensType', 'label'], activeProduct),
    is_eligible_for_hto: Boolean(activeProduct.eligibleForHto),
    prescription: getPrescriptionValues(wizard, meta),
    productAsHto: Boolean(meta.productAsHTO),
    custom_options: activeUsage.usageName,
    isPremium: Boolean((activeProduct.badges || []).find(propEq('id', BADGES.PREMIUM))),
    pack: {
        name: pathOr(activeProduct.contactLensPackageDetails, ['title'], activePackage),
    },
    options: {
        0: {
            option: {alias: 'package'},
            title: activePackage.title,
        },
    },
    discounted_price: wizard?.discountPrice?.finalPrice || activeProduct.finalPrice,
    badges: emptyByDefault(activeProduct.badges),
    url: emptyByDefault(activeProduct.url),
});
